import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { controlesApi } from '../services/api';
import './ControleDocumentaire.css';

// Module « Contrôle documentaire » (Labo Tools) — l'IA (Mistral) vérifie, pour
// chaque fichier déposé (un dossier scanné, ex. un OR de garantie), la présence
// des pièces listées dans une grille configurable.
// Niveau 1 : onglets Campagnes / Grilles. Niveau 2 : une campagne = matrice
// dossiers × points, corrigeable, exportable.

const STATUTS = {
  present:        { label: 'Présent',        court: '✓',   cls: 'badge-success' },
  partiel:        { label: 'Partiel',        court: '½',   cls: 'badge-warning' },
  absent:         { label: 'Absent',         court: '✗',   cls: 'badge-error' },
  non_applicable: { label: 'Non applicable', court: 'N/A', cls: 'badge-neutral' },
  incertain:      { label: 'Incertain',      court: '?',   cls: 'badge-info' },
};
const ETATS = {
  conforme:     { label: 'Conforme',     cls: 'badge-success' },
  non_conforme: { label: 'Non conforme', cls: 'badge-error' },
  a_verifier:   { label: 'À vérifier',   cls: 'badge-warning' },
  en_cours:     { label: 'En cours',     cls: 'badge-info' },
  erreur:       { label: 'Erreur',       cls: 'badge-error' },
};
// Familles de points : ce que l'IA vérifie. Pour une pièce on dit « présent »,
// pour un contenu ou une cohérence « conforme » (même statut stocké).
const FAMILLES = {
  presence:  { label: 'Présence des pièces',    ok: 'Présent',  ko: 'Absent' },
  contenu:   { label: 'Contenu des pièces',     ok: 'Conforme', ko: 'Non conforme' },
  coherence: { label: 'Cohérence entre pièces', ok: 'Conforme', ko: 'Non conforme' },
  regles:    { label: 'Règles techniques',      ok: 'Conforme', ko: 'Non conforme' },
};
const famille = (p) => (FAMILLES[p?.famille] ? p.famille : 'presence');
const libelleStatut = (statut, p) => {
  if (statut === 'present') return FAMILLES[famille(p)].ok;
  if (statut === 'absent') return FAMILLES[famille(p)].ko;
  return STATUTS[statut]?.label || '';
};
// Points consécutifs d'une même famille → groupes (en-têtes de la matrice, détail)
const grouper = (points) => points.reduce((acc, p, i) => {
  const f = famille(p);
  if (!acc.length || acc[acc.length - 1].famille !== f) acc.push({ famille: f, points: [] });
  acc[acc.length - 1].points.push({ ...p, num: i + 1 });
  return acc;
}, []);
const TYPES_ACCEPTES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/tiff'];

const IconImport = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: 4 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
);
const IconExport = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: 4 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);

const errMsg = (e) => e.response?.data?.error || e.message || 'Erreur';
const dateFr = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const normalise = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export default function ControleDocumentaire({ user, setHeaderAction, editMode }) {
  const [tab, setTab] = useState('campagnes');
  const [campagne, setCampagne] = useState(null);
  const [modal, setModal] = useState(null); // 'campagne' | 'grille'
  const [tick, setTick] = useState(0);
  const canDelete = editMode && user?.role === 'super-admin';

  useEffect(() => {
    if (!setHeaderAction || campagne) return;
    setHeaderAction(tab === 'campagnes'
      ? { primary: { label: '+ Nouvelle campagne', onClick: () => setModal('campagne') } }
      : { primary: { label: '+ Nouvelle grille', onClick: () => setModal('grille') } });
  }, [tab, campagne, setHeaderAction]);

  if (campagne) {
    return <CampagneView campagne={campagne} canDelete={canDelete} setHeaderAction={setHeaderAction}
      onBack={() => { setCampagne(null); setTick((t) => t + 1); }} />;
  }

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'campagnes' ? 'active' : ''} onClick={() => setTab('campagnes')}>📋 Campagnes</button>
        <button className={tab === 'grilles' ? 'active' : ''} onClick={() => setTab('grilles')}>⚙️ Grilles de contrôle</button>
      </div>
      {tab === 'campagnes'
        ? <CampagnesTab key={`c${tick}`} canDelete={canDelete} onOpen={setCampagne} />
        : <GrillesTab key={`g${tick}`} canDelete={canDelete} />}

      {modal === 'campagne' && (
        <NouvelleCampagneModal onClose={() => setModal(null)}
          onCreated={(c) => { setModal(null); setCampagne(c); }} />
      )}
      {modal === 'grille' && (
        <GrilleModal grille={null} onClose={() => { setModal(null); setTick((t) => t + 1); }} />
      )}
    </div>
  );
}

// ═══════════════════════════ Campagnes ═══════════════════════════════════════
function CampagnesTab({ canDelete, onOpen }) {
  const [campagnes, setCampagnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try { const r = await controlesApi.listCampagnes(); setCampagnes(r.data.campagnes); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const enCours = campagnes.some((c) => c.nb_en_cours > 0);
  useEffect(() => {
    if (!enCours) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [enCours, load]);

  const supprimer = async (c) => {
    if (!window.confirm(`Supprimer la campagne « ${c.nom} » et ses ${c.nb_documents} fichier(s) ?`)) return;
    try { await controlesApi.deleteCampagne(c.id); await load(); } catch (e) { alert(errMsg(e)); }
  };

  const visibles = campagnes.filter((c) => !search || normalise(`${c.nom} ${c.grille_nom}`).includes(normalise(search)));

  return (
    <>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Campagne</th><th>Grille</th><th className="cd-num">Fichiers</th>
              <th className="cd-num">Conformes</th><th className="cd-num">Non conformes</th>
              <th className="cd-num">À vérifier</th><th className="cd-num">En cours</th>
              <th>Mise à jour</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="cd-empty">Chargement…</td></tr>}
            {!loading && visibles.length === 0 && (
              <tr><td colSpan={9} className="cd-empty">Aucune campagne. Créez-en une avec « + Nouvelle campagne », puis importez vos fichiers.</td></tr>
            )}
            {visibles.map((c) => (
              <tr key={c.id} className="cd-clickable" onClick={() => onOpen(c)}>
                <td className="cd-strong">{c.nom}</td>
                <td>{c.grille_nom || <span className="cd-muted">grille supprimée</span>}</td>
                <td className="cd-num">{c.nb_documents}</td>
                <td className="cd-num">{c.nb_conformes > 0 && <span className="badge-pill badge-success">{c.nb_conformes}</span>}</td>
                <td className="cd-num">{c.nb_non_conformes > 0 && <span className="badge-pill badge-error">{c.nb_non_conformes}</span>}</td>
                <td className="cd-num">{c.nb_a_verifier > 0 && <span className="badge-pill badge-warning">{c.nb_a_verifier}</span>}</td>
                <td className="cd-num">{(c.nb_en_cours + c.nb_erreurs) > 0 && (
                  <span className={`badge-pill ${c.nb_erreurs ? 'badge-error' : 'badge-info'}`}>
                    {c.nb_en_cours}{c.nb_erreurs ? ` · ${c.nb_erreurs} err.` : ''}
                  </span>)}
                </td>
                <td className="cd-muted">{dateFr(c.updated_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Ouvrir" onClick={() => onOpen(c)}>👁️</button>
                    {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => supprimer(c)}>🗑️</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function NouvelleCampagneModal({ onClose, onCreated }) {
  const [grilles, setGrilles] = useState([]);
  const [form, setForm] = useState({ nom: '', grille_id: '' });
  const [err, setErr] = useState('');
  useEffect(() => {
    controlesApi.listGrilles().then((r) => {
      setGrilles(r.data.grilles);
      setForm((f) => ({ ...f, grille_id: r.data.grilles[0]?.id || '' }));
    });
  }, []);
  const save = async () => {
    if (!form.nom.trim()) return setErr('Le nom est requis');
    if (!form.grille_id) return setErr('Choisissez une grille de contrôle');
    try {
      const r = await controlesApi.createCampagne({ nom: form.nom, grille_id: Number(form.grille_id) });
      const g = grilles.find((x) => x.id === Number(form.grille_id));
      onCreated({ ...r.data.campagne, grille_nom: g?.nom });
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Modal title="Nouvelle campagne de contrôle" onClose={onClose} onSave={save} saveLabel="Créer">
      {err && <div className="cd-banner status-error">{err}</div>}
      <div className="form-group">
        <label>Nom de la campagne *</label>
        <input autoFocus value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex. Audit Ford janvier 2026" />
      </div>
      <div className="form-group">
        <label>Grille de contrôle *</label>
        <select value={form.grille_id} onChange={(e) => setForm({ ...form, grille_id: e.target.value })}>
          {grilles.map((g) => <option key={g.id} value={g.id}>{g.nom} ({g.nb_points} points)</option>)}
        </select>
      </div>
      <p className="cd-hint">La grille fixe les points que l'IA vérifie dans chaque fichier. Elle se modifie dans l'onglet « Grilles de contrôle ».</p>
    </Modal>
  );
}

// ═══════════════════════════ Grilles ═════════════════════════════════════════
function GrillesTab({ canDelete }) {
  const [grilles, setGrilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try { const r = await controlesApi.listGrilles(); setGrilles(r.data.grilles); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const supprimer = async (g) => {
    if (!window.confirm(`Supprimer la grille « ${g.nom} » et ses points ?`)) return;
    try { await controlesApi.deleteGrille(g.id); await load(); } catch (e) { alert(errMsg(e)); }
  };
  const visibles = grilles.filter((g) => !search || normalise(g.nom).includes(normalise(search)));

  return (
    <>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="table-container">
        <table>
          <thead><tr><th>Grille</th><th>Identifiant relevé dans chaque dossier</th><th className="cd-num">Points</th><th className="cd-num">Campagnes</th><th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="cd-empty">Chargement…</td></tr>}
            {!loading && visibles.length === 0 && <tr><td colSpan={5} className="cd-empty">Aucune grille.</td></tr>}
            {visibles.map((g) => (
              <tr key={g.id} className="cd-clickable" onClick={() => setEdit(g)}>
                <td className="cd-strong">{g.nom}</td>
                <td className="cd-muted">{g.reference_libelle || '—'}</td>
                <td className="cd-num">{g.nb_points}</td>
                <td className="cd-num">{g.nb_campagnes}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => setEdit(g)}>✏️</button>
                    {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => supprimer(g)}>🗑️</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <GrilleModal grille={edit} onClose={() => { setEdit(null); load(); }} />}
    </>
  );
}

// Grille : paramètres généraux + points (un point s'ajoute une fois la grille créée)
function GrilleModal({ grille, onClose }) {
  const [g, setG] = useState(grille || { nom: '', reference_libelle: '', consignes: '' });
  const [points, setPoints] = useState([]);
  const [pointEdit, setPointEdit] = useState(null);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  const loadPoints = useCallback(async (id) => {
    if (!id) return;
    const r = await controlesApi.listPoints(id); setPoints(r.data.points);
  }, []);
  useEffect(() => { loadPoints(g.id); }, [g.id, loadPoints]);

  const saveGrille = async () => {
    if (!g.nom?.trim()) return setErr('Le nom est requis');
    try {
      const payload = { nom: g.nom, reference_libelle: g.reference_libelle, consignes: g.consignes, description: g.description };
      const r = g.id ? await controlesApi.updateGrille(g.id, payload) : await controlesApi.createGrille(payload);
      setG(r.data.grille); setErr(''); setSaved(true);
    } catch (e) { setErr(errMsg(e)); }
  };
  const savePoint = async () => {
    if (!pointEdit.libelle?.trim()) return;
    const payload = {
      libelle: pointEdit.libelle, consigne: pointEdit.consigne || '', obligatoire: pointEdit.obligatoire,
      famille: famille(pointEdit), code_motif: pointEdit.code_motif || '',
    };
    if (pointEdit.id) await controlesApi.updatePoint(pointEdit.id, payload);
    else await controlesApi.createPoint(g.id, payload);
    setPointEdit(null); loadPoints(g.id);
  };
  const delPoint = async (p) => {
    if (!window.confirm(`Supprimer le point « ${p.libelle} » ? Ses résultats dans les campagnes seront supprimés.`)) return;
    await controlesApi.deletePoint(p.id); loadPoints(g.id);
  };
  const move = async (p, dir) => {
    const i = points.findIndex((x) => x.id === p.id); const o = points[i + dir];
    if (!o) return;
    await controlesApi.updatePoint(p.id, { ordre: o.ordre });
    await controlesApi.updatePoint(o.id, { ordre: p.ordre });
    loadPoints(g.id);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large cd-grille-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{g.id ? `Grille — ${g.nom}` : 'Nouvelle grille de contrôle'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="cd-banner status-error">{err}</div>}
          <div className="form-group">
            <label>Nom *</label>
            <input value={g.nom || ''} onChange={(e) => { setG({ ...g, nom: e.target.value }); setSaved(false); }} placeholder="Ex. Audit garantie Ford" />
          </div>
          <div className="form-group">
            <label>Identifiant à relever dans chaque dossier</label>
            <input value={g.reference_libelle || ''} onChange={(e) => { setG({ ...g, reference_libelle: e.target.value }); setSaved(false); }} placeholder="Ex. N° de demande de garantie (n° d'OR + n° de JOB)" />
          </div>
          <div className="form-group">
            <label>Consignes générales pour l'IA</label>
            <textarea rows={3} value={g.consignes || ''} onChange={(e) => { setG({ ...g, consignes: e.target.value }); setSaved(false); }}
              placeholder="Ex. Chaque fichier est le dossier scanné d'une demande de garantie. La première page est une page de garde à ignorer." />
          </div>
          <div className="cd-grille-save">
            <button className="btn-primary" onClick={saveGrille}>{g.id ? 'Enregistrer' : 'Créer la grille'}</button>
            {saved && <span className="cd-muted">Enregistré.</span>}
          </div>

          {g.id && (
            <>
              <div className="cd-section-head">
                <h3>Points à contrôler ({points.length})</h3>
                <button className="btn-secondary" onClick={() => setPointEdit({ libelle: '', consigne: '', obligatoire: true, famille: 'presence', code_motif: '' })}>+ Ajouter un point</button>
              </div>
              <div className="table-container">
                <table>
                  <thead><tr><th className="cd-num">#</th><th>Point</th><th>Famille</th><th>Consigne IA</th><th>Exigence</th><th></th></tr></thead>
                  <tbody>
                    {points.length === 0 && <tr><td colSpan={6} className="cd-empty">Aucun point. Ajoutez les pièces ou critères à vérifier.</td></tr>}
                    {points.map((p, i) => (
                      <tr key={p.id}>
                        <td className="cd-num cd-muted">{i + 1}</td>
                        <td>
                          <div className="cd-strong">{p.libelle}</div>
                          {p.code_motif && <div className="cd-code">{p.code_motif}</div>}
                        </td>
                        <td className="cd-small">{FAMILLES[famille(p)].label}</td>
                        <td className="cd-consigne">{p.consigne || <span className="cd-muted">—</span>}</td>
                        <td><span className={`badge-pill ${p.obligatoire ? 'badge-info' : 'badge-neutral'}`}>{p.obligatoire ? 'Obligatoire' : 'Si applicable'}</span></td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon" title="Monter" disabled={i === 0} onClick={() => move(p, -1)}>↑</button>
                            <button className="btn-icon" title="Descendre" disabled={i === points.length - 1} onClick={() => move(p, 1)}>↓</button>
                            <button className="btn-icon" title="Modifier" onClick={() => setPointEdit({ ...p })}>✏️</button>
                            <button className="btn-icon delete" title="Supprimer" onClick={() => delPoint(p)}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="cd-hint">
                Familles : la présence d'une pièce, le contenu d'une pièce (ex. le rapport technicien explique-t-il la cause ?),
                la cohérence entre pièces (ex. mêmes pièces sur la demande et en sortie de stock), une règle technique.
                Le code motif reprend celui de l'auditeur (ex. D01.05) pour lire les résultats dans son vocabulaire.
                « Obligatoire » : le point est vérifié dans chaque dossier. « Si applicable » : elle n'est exigée que si le dossier
                y fait référence (ex. un n° de hotline cité) ; sinon le point est classé « non applicable ».
                Une modification de la grille ne s'applique aux fichiers déjà analysés qu'après « Relancer l'analyse » dans la campagne.
              </p>
            </>
          )}
        </div>
        <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Fermer</button></div>
      </div>

      {pointEdit && (
        <Modal title={pointEdit.id ? 'Modifier le point' : 'Nouveau point à contrôler'} onClose={() => setPointEdit(null)} onSave={savePoint}>
          <div className="form-group">
            <label>Libellé *</label>
            <input autoFocus value={pointEdit.libelle} onChange={(e) => setPointEdit({ ...pointEdit, libelle: e.target.value })} placeholder="Ex. Rapport Hotline" />
          </div>
          <div className="form-group">
            <label>Famille</label>
            <select value={famille(pointEdit)} onChange={(e) => setPointEdit({ ...pointEdit, famille: e.target.value })}>
              {Object.entries(FAMILLES).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Code motif (facultatif)</label>
            <input value={pointEdit.code_motif || ''} onChange={(e) => setPointEdit({ ...pointEdit, code_motif: e.target.value })} placeholder="Ex. D01.05" />
          </div>
          <div className="form-group">
            <label>Consigne pour l'IA (ce qui rend le point satisfait ou non)</label>
            <textarea rows={4} value={pointEdit.consigne || ''} onChange={(e) => setPointEdit({ ...pointEdit, consigne: e.target.value })}
              placeholder="Décrivez la pièce ou le critère attendu, ce qui le rend partiel, ce qui le rend non conforme…" />
          </div>
          <div className="form-group">
            <label>Exigence</label>
            <select value={pointEdit.obligatoire ? '1' : '0'} onChange={(e) => setPointEdit({ ...pointEdit, obligatoire: e.target.value === '1' })}>
              <option value="1">Obligatoire — dans chaque dossier</option>
              <option value="0">Si applicable — seulement si le dossier y fait référence</option>
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════ Campagne (matrice) ══════════════════════════════
function CampagneView({ campagne, canDelete, setHeaderAction, onBack }) {
  const [data, setData] = useState({ points: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [onglet, setOnglet] = useState('tous');
  const [search, setSearch] = useState('');
  const [pointFiltre, setPointFiltre] = useState('');
  const [selection, setSelection] = useState([]);
  const [uploads, setUploads] = useState(null); // { total, faits, echecs: [] }
  const [detail, setDetail] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await controlesApi.resultats(campagne.id); setData(r.data); } finally { setLoading(false); }
  }, [campagne.id]);
  useEffect(() => { load(); }, [load]);

  const enCours = data.rows.some((r) => r.etat === 'en_cours');
  useEffect(() => {
    if (!enCours) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [enCours, load]);

  // Import en masse : envoi direct vers S3 (≈5 en parallèle), repli multipart.
  const importer = useCallback(async (fileList) => {
    const files = Array.from(fileList).filter((f) => TYPES_ACCEPTES.includes(f.type));
    const ignores = Array.from(fileList).filter((f) => !TYPES_ACCEPTES.includes(f.type)).map((f) => `${f.name} (format non accepté)`);
    if (!files.length) { if (ignores.length) alert(`Aucun fichier accepté (PDF ou image).`); return; }
    setUploads({ total: files.length, faits: 0, echecs: ignores });
    const envoyer = async (file) => {
      try {
        const { data: p } = await controlesApi.presign(campagne.id, { filename: file.name, contentType: file.type });
        const put = await fetch(p.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (!put.ok) throw new Error('S3');
        await controlesApi.confirm(campagne.id, { key: p.key, filename: file.name, contentType: file.type, size: file.size });
      } catch (_) {
        const form = new FormData(); form.append('file', file);
        await controlesApi.upload(campagne.id, form);
      }
    };
    let i = 0;
    const worker = async () => {
      while (i < files.length) {
        const f = files[i++];
        try { await envoyer(f); setUploads((u) => ({ ...u, faits: u.faits + 1 })); }
        catch (_) { setUploads((u) => ({ ...u, faits: u.faits + 1, echecs: [...u.echecs, f.name] })); }
      }
    };
    await Promise.all(Array.from({ length: Math.min(5, files.length) }, worker));
    load();
  }, [campagne.id, load]);

  const relancerTout = useCallback(async () => {
    if (!window.confirm("Relancer l'analyse IA de tous les fichiers ? Les corrections manuelles sont conservées.")) return;
    try { await controlesApi.analyser(campagne.id); load(); } catch (e) { alert(errMsg(e)); }
  }, [campagne.id, load]);

  // Actions d'en-tête posées une seule fois ; elles lisent l'état courant par ref
  // (sinon chaque rendu repose l'en-tête, qui re-rend la page : boucle).
  const actionsRef = useRef({});
  actionsRef.current = { onBack, relancerTout, exporter: () => exportExcel(campagne, data) };
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      secondary: [
        { label: '← Campagnes', onClick: () => actionsRef.current.onBack() },
        { label: "🔄 Relancer l'analyse", onClick: () => actionsRef.current.relancerTout() },
        { label: <><IconExport />Export Excel</>, onClick: () => actionsRef.current.exporter() },
      ],
      primary: { label: <><IconImport />Importer des fichiers</>, onClick: () => fileRef.current?.click() },
    });
  }, [setHeaderAction]);

  const compte = useMemo(() => {
    const c = { tous: data.rows.length, non_conforme: 0, a_verifier: 0, conforme: 0, en_cours: 0 };
    for (const r of data.rows) {
      if (r.etat === 'erreur') c.en_cours++; else c[r.etat]++;
    }
    return c;
  }, [data.rows]);

  const visibles = data.rows.filter((r) => {
    if (onglet !== 'tous') {
      const etat = r.etat === 'erreur' ? 'en_cours' : r.etat;
      if (etat !== onglet) return false;
    }
    if (pointFiltre && !['absent', 'partiel', 'incertain'].includes(r.resultats[pointFiltre]?.statut)) return false;
    if (search && !normalise(`${r.nom} ${r.reference || ''}`).includes(normalise(search))) return false;
    return true;
  });

  const toggle = (id) => setSelection((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const supprimerSelection = async () => {
    if (!window.confirm(`Supprimer ${selection.length} fichier(s) et leurs résultats ?`)) return;
    try { await controlesApi.deleteFichiers(campagne.id, selection); setSelection([]); load(); } catch (e) { alert(errMsg(e)); }
  };
  const relancer = async (r) => { await controlesApi.relancer(campagne.id, r.id); load(); };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) importer(e.dataTransfer.files); };

  return (
    <div className={`page-container${dragOver ? ' cd-dragover' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={onDrop}>
      <input ref={fileRef} type="file" multiple hidden accept={TYPES_ACCEPTES.join(',')}
        onChange={(e) => { importer(e.target.files); e.target.value = ''; }} />

      <div className="page-tabs">
        <button className={onglet === 'tous' ? 'active' : ''} onClick={() => setOnglet('tous')}>📂 Tous ({compte.tous})</button>
        <button className={onglet === 'non_conforme' ? 'active' : ''} onClick={() => setOnglet('non_conforme')}>❌ Non conformes ({compte.non_conforme})</button>
        <button className={onglet === 'a_verifier' ? 'active' : ''} onClick={() => setOnglet('a_verifier')}>⚠️ À vérifier ({compte.a_verifier})</button>
        <button className={onglet === 'conforme' ? 'active' : ''} onClick={() => setOnglet('conforme')}>✅ Conformes ({compte.conforme})</button>
        <button className={onglet === 'en_cours' ? 'active' : ''} onClick={() => setOnglet('en_cours')}>⏳ En cours / erreurs ({compte.en_cours})</button>
      </div>

      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className={`page-select${pointFiltre ? ' active-filter' : ''}`} value={pointFiltre} onChange={(e) => setPointFiltre(e.target.value)}>
          <option value="">Tous les points</option>
          {data.points.map((p) => <option key={p.id} value={p.id}>Manquant ou douteux : {p.libelle}</option>)}
        </select>
        {canDelete && selection.length > 0 && (
          <button className="btn-secondary" onClick={supprimerSelection}>🗑️ Supprimer la sélection ({selection.length})</button>
        )}
      </div>

      <div className="cd-campagne-info">
        <span className="cd-strong">{campagne.nom}</span>
        {campagne.grille_nom && <span className="cd-muted">· grille « {campagne.grille_nom} »</span>}
        {uploads && (
          <span className={`badge-pill ${uploads.faits < uploads.total ? 'badge-info' : uploads.echecs.length ? 'badge-warning' : 'badge-success'}`}>
            {uploads.faits < uploads.total ? `Envoi ${uploads.faits}/${uploads.total}…` : `${uploads.total - uploads.echecs.length} fichier(s) envoyé(s)`}
            {uploads.echecs.length > 0 && ` · ${uploads.echecs.length} échec(s)`}
          </span>
        )}
        {enCours && <span className="badge-pill badge-info">Lecture et analyse IA en cours…</span>}
      </div>

      <div className="table-container cd-matrix">
        <table>
          <thead>
            <tr className="cd-familles">
              {canDelete && <th rowSpan={2} className="cd-check"></th>}
              <th rowSpan={2} className="cd-col-fichier">Dossier</th>
              {grouper(data.points).map((g, i) => (
                <th key={i} colSpan={g.points.length} className="cd-famille-th">{FAMILLES[g.famille].label}</th>
              ))}
              <th rowSpan={2}>État</th>
              <th rowSpan={2}></th>
            </tr>
            <tr>
              {data.points.map((p, i) => (
                <th key={p.id} className="cd-col-point" title={`${p.libelle}${p.obligatoire ? '' : ' (si applicable)'}${p.code_motif ? ` — ${p.code_motif}` : ''}`}>
                  <span className="cd-point-num">{i + 1}</span> {p.libelle}
                  {p.code_motif && <div className="cd-code">{p.code_motif}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={data.points.length + 4} className="cd-empty">Chargement…</td></tr>}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={data.points.length + 4} className="cd-empty">
                Aucun fichier. Glissez vos PDF ou images sur la page, ou utilisez « Importer des fichiers » — plusieurs dizaines à la fois.
                Chaque fichier est un dossier : l'IA le lit puis vérifie chaque point de la grille.
              </td></tr>
            )}
            {!loading && data.rows.length > 0 && visibles.length === 0 && (
              <tr><td colSpan={data.points.length + 4} className="cd-empty">Aucun dossier ne correspond aux filtres.</td></tr>
            )}
            {visibles.map((r) => (
              <tr key={r.id}>
                {canDelete && <td className="cd-check"><input type="checkbox" checked={selection.includes(r.id)} onChange={() => toggle(r.id)} /></td>}
                <td className="cd-col-fichier cd-clickable" onClick={() => setDetail(r)}>
                  <div className="cd-strong">{r.reference || r.nom}</div>
                  <div className="cd-muted cd-small">{r.reference ? `${r.nom} · ` : ''}{r.nb_pages ? `${r.nb_pages} p.` : ''}</div>
                </td>
                {data.points.map((p) => {
                  const res = r.resultats[p.id];
                  const s = res && STATUTS[res.statut];
                  return (
                    <td key={p.id} className="cd-cell cd-clickable" onClick={() => setDetail(r)}
                      title={res ? `${libelleStatut(res.statut, p)}${res.edite_manuellement ? ' (corrigé à la main)' : ''}${res.justification ? ` — ${res.justification}` : ''}` : ''}>
                      {s ? <span className={`badge-pill ${s.cls}`}>{s.court}{res.edite_manuellement ? ' ✎' : ''}</span> : <span className="cd-muted">·</span>}
                    </td>
                  );
                })}
                <td>
                  <span className={`badge-pill ${ETATS[r.etat].cls}`}>
                    {r.etat === 'en_cours' ? (r.statut === 'lu' ? 'Analyse…' : 'Lecture…') : ETATS[r.etat].label}
                  </span>
                  {r.etat === 'erreur' && r.erreur && <div className="cd-erreur" title={r.erreur}>{r.erreur}</div>}
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Voir le détail" onClick={() => setDetail(r)}>👁️</button>
                    {r.etat !== 'en_cours' && <button className="btn-icon" title="Relancer ce fichier" onClick={() => relancer(r)}>🔄</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="cd-hint">
        Légende : ✓ présent ou conforme · ½ partiel · ✗ absent ou non conforme · N/A non applicable · ? incertain · ✎ corrigé à la main.
        Un dossier est conforme quand tous ses points sont présents ou non applicables ; « incertain » le met « à vérifier ».
        L'IA juge sur le contenu des pages : une page de garde cochée à la main n'est jamais retenue comme preuve. Elle peut se tromper — ouvrez un dossier pour corriger.
      </p>

      {detail && (
        <DossierModal campagne={campagne} points={data.points}
          row={data.rows.find((x) => x.id === detail.id) || detail}
          onClose={() => setDetail(null)} onChange={load} />
      )}
    </div>
  );
}

// Détail d'un dossier : points + justification + correction, aperçu du fichier
function DossierModal({ campagne, points, row, onClose, onChange }) {
  const [url, setUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [commentaires, setCommentaires] = useState({});

  useEffect(() => {
    controlesApi.fileUrl(campagne.id, row.id).then((r) => setUrl(r.data.url)).catch(() => setUrl(null));
  }, [campagne.id, row.id]);

  const corriger = async (pointId, statut) => {
    try {
      await controlesApi.corriger(campagne.id, {
        document_id: row.id, point_id: pointId, statut,
        commentaire: commentaires[pointId] ?? row.resultats[pointId]?.commentaire ?? '',
      });
      onChange();
    } catch (e) { alert(errMsg(e)); }
  };
  const isPdf = row.type_mime === 'application/pdf';
  const src = url && (isPdf ? `${url}#page=${page}` : url);

  return (
    <div className="modal-overlay">
      <div className="modal-content large cd-dossier-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{row.reference || row.nom} <span className={`badge-pill ${ETATS[row.etat].cls}`}>{ETATS[row.etat].label}</span></h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body cd-dossier-body">
          <div className="cd-dossier-points">
            <div className="cd-muted cd-small">{row.nom}{row.nb_pages ? ` · ${row.nb_pages} pages` : ''}{row.analyse_at ? ` · analysé le ${dateFr(row.analyse_at)}` : ''}</div>
            {row.synthese && <p className="cd-synthese">{row.synthese}</p>}
            {row.erreur && <div className="cd-banner status-error">{row.erreur}</div>}
            {grouper(points).map((g) => (
              <React.Fragment key={`${g.famille}-${g.points[0].id}`}>
              <h3 className="cd-famille-titre">{FAMILLES[g.famille].label}</h3>
              {g.points.map((p) => {
              const res = row.resultats[p.id];
              return (
                <div key={p.id} className="cd-point">
                  <div className="cd-point-head">
                    <span className="cd-point-num">{p.num}</span>
                    <span className="cd-strong">{p.libelle}</span>
                    {p.code_motif && <span className="cd-code">{p.code_motif}</span>}
                    {!p.obligatoire && <span className="cd-muted cd-small">(si applicable)</span>}
                    <select className={`cd-statut-select ${res ? STATUTS[res.statut].cls : ''}`} value={res?.statut || ''}
                      onChange={(e) => corriger(p.id, e.target.value)}>
                      {!res && <option value="">—</option>}
                      {Object.keys(STATUTS).map((k) => <option key={k} value={k}>{libelleStatut(k, p)}</option>)}
                    </select>
                  </div>
                  {res?.justification && <div className="cd-justif">{res.justification}</div>}
                  {res?.pages && (
                    <div className="cd-pages">Pages : {res.pages.split(',').map((n) => (
                      <button key={n} className="cd-page-link" onClick={() => setPage(Number(n))} disabled={!isPdf}>{n}</button>
                    ))}</div>
                  )}
                  {res?.edite_manuellement && (
                    <div className="cd-small cd-muted">✎ Corrigé à la main{res.modifie_par_prenom ? ` par ${res.modifie_par_prenom} ${res.modifie_par_nom}` : ''} le {dateFr(res.updated_at)} — conservé si l'analyse est relancée.</div>
                  )}
                  <input className="cd-commentaire" placeholder="Commentaire (facultatif, enregistré avec une correction)"
                    value={commentaires[p.id] ?? res?.commentaire ?? ''}
                    onChange={(e) => setCommentaires({ ...commentaires, [p.id]: e.target.value })}
                    onBlur={() => { if (res && commentaires[p.id] !== undefined && commentaires[p.id] !== (res.commentaire || '')) corriger(p.id, res.statut); }} />
                </div>
              );
              })}
              </React.Fragment>
            ))}
            {Array.isArray(row.inventaire) && row.inventaire.length > 0 && (
              <details className="cd-inventaire">
                <summary>Contenu du dossier, page par page (lu par l'IA)</summary>
                <ul>
                  {row.inventaire.map((x) => (
                    <li key={x.page}><button className="cd-page-link" onClick={() => setPage(x.page)} disabled={!isPdf}>{x.page}</button> {x.piece}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <div className="cd-dossier-apercu">
            {src ? <iframe key={src} title="Aperçu" src={src} className="cd-apercu-frame" /> : <div className="cd-empty">Aperçu indisponible</div>}
          </div>
        </div>
        <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Fermer</button></div>
      </div>
    </div>
  );
}

// ── Export Excel : une feuille matrice + une feuille détail ──────────────────
function exportExcel(campagne, { points, rows }) {
  const matrice = [
    ['Dossier', 'Fichier', 'État', ...points.map((p) => (p.code_motif ? `${p.libelle} (${p.code_motif})` : p.libelle)), 'Synthèse IA'],
    ...rows.map((r) => [
      r.reference || '', r.nom, ETATS[r.etat]?.label || r.etat,
      ...points.map((p) => libelleStatut(r.resultats[p.id]?.statut, p)),
      r.synthese || '',
    ]),
  ];
  const detail = [['Dossier', 'Fichier', 'Famille', 'Code motif', 'Point', 'Exigence', 'Statut', 'Pages', 'Justification IA', 'Corrigé à la main', 'Commentaire']];
  for (const r of rows) {
    for (const p of points) {
      const res = r.resultats[p.id];
      detail.push([r.reference || '', r.nom, FAMILLES[famille(p)].label, p.code_motif || '', p.libelle, p.obligatoire ? 'Obligatoire' : 'Si applicable',
        libelleStatut(res?.statut, p), res?.pages || '', res?.justification || '', res?.edite_manuellement ? 'Oui' : '', res?.commentaire || '']);
    }
  }
  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.aoa_to_sheet(matrice);
  ws1['!cols'] = matrice[0].map((h, i) => ({ wch: i === matrice[0].length - 1 ? 60 : Math.min(28, Math.max(12, String(h).length)) }));
  const ws2 = XLSX.utils.aoa_to_sheet(detail);
  ws2['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 80 }, { wch: 10 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Synthèse');
  XLSX.utils.book_append_sheet(wb, ws2, 'Détail');
  XLSX.writeFile(wb, `controle_${campagne.nom.replace(/[^a-zA-Z0-9]+/g, '_')}.xlsx`);
}

function Modal({ title, children, onClose, onSave, saveLabel = 'Enregistrer' }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>{title}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={onSave}>{saveLabel}</button>
        </div>
      </div>
    </div>
  );
}
